import React, { FC, Dispatch, SetStateAction, useState, useEffect } from 'react';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import ArrowForward from '@mui/icons-material/ArrowForward';

import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import TextInput from '../../../sharedModules/form/components/TextInput';
import SelectInput from '../../../sharedModules/form/components/SelectInput';

import { SelectOption } from '../../../sharedModules/form/model';
import { AhriCertificate, CertificateSubmission } from '../../model';
import axios from '../../../sharedModules/utils/axios.utils';

interface NewAssetInputProps {
  certificateSubmission: CertificateSubmission;
  setCertificateSubmission: Dispatch<SetStateAction<CertificateSubmission>>;
  assetInputOpen: boolean;
  setAssetInputOpen: Dispatch<SetStateAction<boolean>>;
  setRequestingCertificate: Dispatch<SetStateAction<boolean>>;
}

const NewAssetInput: FC<NewAssetInputProps> = (props) => {
  let { assetInputOpen, certificateSubmission, setCertificateSubmission, setAssetInputOpen, setRequestingCertificate } =
    props;

  // We use one self-contained state for the loading indicator of the programIds
  // We use a prop for requesting the certificate so that the submissionCollection can respond visually to the certificate being submitted.

  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [manufacturerOptionList, setManufacturerOptionList] = useState<any>([]);
  const [formValues, setFormValues] = useState<AhriCertificate>();
  const [loading, setLoading] = useState(false);
  const [programIdOptions, setProgramIdOptions] = useState<SelectOption[]>([]);

  const selectedManufacturer = React.useMemo(
    () => manufacturerOptionList?.find((o) => o.value === formValues?.manufacturer_id) ?? null,
    [formValues?.manufacturer_id],
  );

  const fetchManufacturerOptionList = async () => {
    const response: any = await axios.get('/pick_list_options', {
      params: { model: 'Manufacturer', 'ransack[manufacturer_types_name_i_cont]': 'Energy Solutions' },
    });
    setManufacturerOptionList(response.data.result);
  };

  useEffect(() => {
    if (certificateSubmission) {
      fetchManufacturerOptionList();
      setFormValues({ ...formValues, certificate_submission_id: certificateSubmission.id });
    }
  }, [certificateSubmission]);

  const handleGetProgramIds = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`energy_solutions/ahri_program_ids/${formValues?.model_number}`);
      let fetchedOptions = response.data;
      setProgramIdOptions(fetchedOptions);
      let firstOptionId = fetchedOptions[0]?.value;
      setFormValues({ ...formValues, program_id: firstOptionId });
    } catch (error) {
      setErrorMessage('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const requestCertificate = async () => {
    setRequestingCertificate(true);
    try {
      const response: any = await axios.post('/energy_solutions/ahri_certificates', formValues);
      setCertificateSubmission(response.data.result);
      setFormValues({
        manufacturer_id: '',
        model_number: '',
        program_id: '',
        certificate_submission_id: certificateSubmission.id,
      });
      setInputValue('');
      setProgramIdOptions([]);
    } catch (error) {
      setErrorMessage('Something went wrong');
    } finally {
      setRequestingCertificate(false);
    }
  };

  useEffect(() => {
    if (formValues?.model_number?.length > 3 && formValues?.program_id) {
      requestCertificate();
    }
  }, [formValues]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') handleGetProgramIds();
  };

  return (
    <LoadingWrapper isLoading={loading}>
      <Accordion expanded={assetInputOpen} onChange={() => setAssetInputOpen(!assetInputOpen)}>
        <AccordionSummary>
          <Typography variant="h5">New Asset Request</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              {manufacturerOptionList?.length > 0 && (
                <Autocomplete
                  freeSolo
                  options={manufacturerOptionList}
                  getOptionLabel={(option: any) => option.label}
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                  value={selectedManufacturer || null}
                  onChange={(event, newValue: any) => setFormValues({ ...formValues, manufacturer_id: newValue.value })}
                  renderInput={(params) => <TextField {...params} label="Manufacturer" variant="outlined" />}
                />
              )}
            </Grid>
            <Grid item xs={12} md={4} style={{ paddingLeft: 5, paddingRight: 5 }} onKeyDown={handleKeyDown}>
              <TextInput
                value={formValues?.model_number}
                label="Model"
                valueChanged={(value) => setFormValues({ ...formValues, model_number: value })}
              />
            </Grid>
            <Grid item xs={12} md={4} style={{ display: 'flex', alignItems: 'center' }}>
              {programIdOptions?.length > 0 ? (
                <>
                  {programIdOptions?.length === 1 ? (
                    <Typography>{programIdOptions[0].label}</Typography>
                  ) : (
                    <SelectInput
                      label=""
                      choices={programIdOptions}
                      value={formValues?.program_id}
                      valueChanged={(value: string) => setFormValues({ ...formValues, program_id: value })}
                    />
                  )}
                </>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGetProgramIds}
                  disabled={formValues?.model_number?.length < 3}
                >
                  {loading ? <CircularProgress /> : <ArrowForward />}
                </Button>
              )}
              {errorMessage?.length > 0 && <p style={{ marginLeft: 10, color: 'red' }}>{errorMessage}</p>}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </LoadingWrapper>
  );
};

export default NewAssetInput;
